import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _typeof from "@babel/runtime/helpers/typeof";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15, _descriptor16, _descriptor17, _descriptor18, _descriptor19, _descriptor20, _descriptor21, _descriptor22, _QuestionStore;
import { action, computed, observable, toJS } from 'mobx';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { fg } from '@atlaskit/platform-feature-flags';
import { ChoiceMessage, IntlChoiceMessages } from '../components/questions/ChoiceMessages.intl';
import { FormAnswerEmpty, FormQuestionType } from '../models/Form';
import { asyncChoicesFieldSchemaCustoms, asyncChoicesFieldSchemaCustomsWithEntitlement, asyncChoicesFieldSchemaSystems } from '../utils/jiraFieldUtils';
import { getAttachmentValidation, getChoiceValidation, getDateTimeValidation, getDateValidation, getNumberValidation, getRichTextValidation, getTextValidation, getTimeValidation, getUserPickerValidation } from '../utils/validation/validators';

// extracted FormAnswer data, done in extractAnswer()

// was: { id: string; name: string };

// Array.isArray() returns false for mobx ObservableArray - see "known pitfalls" in mobX docs
var isArray = function isArray(value) {
  return !!value && value.length != null && value.splice != null;
};
export var isRichTextQuestionAnswer = function isRichTextQuestionAnswer(answer) {
  return _typeof(answer) === 'object' && answer.hasOwnProperty('content') && answer.hasOwnProperty('version') && answer.hasOwnProperty('type') && answer.type === 'doc';
};
export var toRichTextQuestionAnswer = function toRichTextQuestionAnswer(answer) {
  return isRichTextQuestionAnswer(answer) ? answer : undefined;
};
var isTextQuestionAnswer = function isTextQuestionAnswer(answer) {
  return typeof answer === 'string';
};
export var toTextQuestionAnswer = function toTextQuestionAnswer(answer) {
  return isTextQuestionAnswer(answer) ? answer : undefined;
};
var isDateTimeQuestionAnswer = function isDateTimeQuestionAnswer(answer) {
  return (answer === null || answer === void 0 ? void 0 : answer.date) !== undefined;
};
export var toDateTimeQuestionAnswer = function toDateTimeQuestionAnswer(answer) {
  return isDateTimeQuestionAnswer(answer) ? answer : undefined;
};
export var toDateTimeQuestionAnswerOrEmpty = function toDateTimeQuestionAnswerOrEmpty(answer) {
  return isDateTimeQuestionAnswer(answer) ? answer : {
    date: '',
    time: ''
  };
}; // same default data as in extractAnswer()

var isChoiceQuestionAnswer = function isChoiceQuestionAnswer(answer) {
  return (answer === null || answer === void 0 ? void 0 : answer.choices) !== undefined;
};
export var toChoiceQuestionAnswer = function toChoiceQuestionAnswer(answer) {
  return isChoiceQuestionAnswer(answer) ? answer : undefined;
};
export var toChoiceQuestionAnswerOrEmpty = function toChoiceQuestionAnswerOrEmpty(answer) {
  return isChoiceQuestionAnswer(answer) ? answer : {
    text: '',
    choices: []
  };
};
var isUserPickerQuestionAnswer = function isUserPickerQuestionAnswer(answer) {
  return isArray(answer);
};
export var toUserPickerQuestionAnswer = function toUserPickerQuestionAnswer(answer) {
  return isUserPickerQuestionAnswer(answer) ? answer : undefined;
};
export var toUserPickerQuestionAnswerOrEmpty = function toUserPickerQuestionAnswerOrEmpty(answer) {
  return isUserPickerQuestionAnswer(answer) ? answer : [];
};
export var isAttachmentQuestionAnswer = function isAttachmentQuestionAnswer(answer) {
  return isArray(answer) && answer.every(function (a) {
    return a.id !== undefined;
  });
};
export var toAttachmentQuestionAnswer = function toAttachmentQuestionAnswer(answer) {
  return isAttachmentQuestionAnswer(answer) ? answer : undefined;
};
var extractAnswer = function extractAnswer(questionType, answer) {
  var _console$debug, _console;
  switch (questionType) {
    case FormQuestionType.TextShort:
    case FormQuestionType.TextLong:
    case FormQuestionType.TextParagraph:
    case FormQuestionType.TextEmail:
    case FormQuestionType.TextUrl:
    case FormQuestionType.Number:
      return answer.text || '';
    case FormQuestionType.DateTime:
      return {
        date: answer.date || '',
        time: answer.time || ''
      };
    case FormQuestionType.Date:
      return answer.date || '';
    case FormQuestionType.Time:
      return answer.time || '';
    case FormQuestionType.ChoiceCascading:
    case FormQuestionType.ChoiceSingle:
    case FormQuestionType.ChoiceMultiple:
    case FormQuestionType.ChoiceDropdown:
    case FormQuestionType.ChoiceDropdownMultiple:
    case FormQuestionType.CmdbObject:
      return {
        text: answer.text || '',
        choices: answer.choices || []
      };
    case FormQuestionType.UserSingle:
    case FormQuestionType.UserMultiple:
      return answer.users || [];
    case FormQuestionType.RichTextParagraph:
      return answer.adf || getEmptyADF();
    case FormQuestionType.Attachment:
      return answer.files || [];
    default:
      // eslint-disable-next-line no-console
      (_console$debug = (_console = console).debug) === null || _console$debug === void 0 || _console$debug.call(_console, "Question type ".concat(questionType, " is not handled."));
      return '';
  }
};
var sortById = function sortById(a, b) {
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
};
export var QuestionStore = (_class = (_QuestionStore = /*#__PURE__*/function () {
  function QuestionStore(intl, questionKey, _formQuestion, loadChoicesAsync, onFieldChange, populateDefaultValueOnEmptySearch, _currentAnswer, onFieldDirty) {
    var _this = this,
      _formQuestion$conditi;
    _classCallCheck(this, QuestionStore);
    this.intl = intl;
    this.id = void 0;
    this.formQuestion = void 0;
    this.conditions = void 0;
    this.hasJiraField = void 0;
    this.hasDataConnection = void 0;
    _initializerDefineProperty(this, "answer", _descriptor, this);
    _initializerDefineProperty(this, "modifiedAnswer", _descriptor2, this);
    _initializerDefineProperty(this, "validating", _descriptor3, this);
    _initializerDefineProperty(this, "loading", _descriptor4, this);
    _initializerDefineProperty(this, "isUploadingMedia", _descriptor5, this);
    _initializerDefineProperty(this, "choices", _descriptor6, this);
    _initializerDefineProperty(this, "answerChoicesAsObjects", _descriptor7, this);
    _initializerDefineProperty(this, "dependentFieldChangedCount", _descriptor8, this);
    _initializerDefineProperty(this, "otherChoiceId", _descriptor9, this);
    _initializerDefineProperty(this, "revisionToken", _descriptor0, this);
    _initializerDefineProperty(this, "issueFieldData", _descriptor1, this);
    this.loadChoicesAsync = void 0;
    this.onFieldChange = void 0;
    this.populateDefaultValueOnEmptySearch = void 0;
    this.onFieldDirty = void 0;
    this.attachmentEqual = function (a1, a2) {
      if (a1 === undefined || a2 === undefined) {
        return false;
      }
      return a1['id'] === a2['id'];
    };
    this.hasAttachmentFieldModified = function () {
      var af1 = _this.answer;
      var af2 = _this.modifiedAnswer;
      if (af1 === undefined || af2 === undefined) {
        return false;
      }
      return !(af1.length === af2.length && af1.every(function (a, idx) {
        return _this.attachmentEqual(a, af2[idx]);
      }));
    };
    this.choiceToOption = function (choice) {
      var _choice$children;
      return {
        value: choice.id,
        label: choice.label,
        children: (_choice$children = choice.children) === null || _choice$children === void 0 ? void 0 : _choice$children.map(_this.choiceToOption),
        cmdbObject: choice.cmdbObject
      };
    };
    this.availableOptions = function () {
      return _this.choices.map(_this.choiceToOption);
    };
    /**
     * Use only for choices fields
     */
    this.getAnswerChoicesAsObjects = function () {
      var choices = _this.answerChoicesAsObjects;
      var currentChoiceOptions = choices.map(_this.choiceToOption);
      var choiceAnswer = toChoiceQuestionAnswerOrEmpty(_this.currentAnswer);
      return currentChoiceOptions.filter(function (option) {
        return choiceAnswer.choices.includes(option.value) ||
        // If the answer is a cascading response, this will return the parent choice
        choiceAnswer.choices.map(function (choice) {
          return choice.split(':')[0];
        }).includes(option.value);
      });
    };
    _initializerDefineProperty(this, "setLastUpdated", _descriptor10, this);
    _initializerDefineProperty(this, "setAnswer", _descriptor11, this);
    _initializerDefineProperty(this, "doPopulateDefaultValueOnEmptySearch", _descriptor12, this);
    _initializerDefineProperty(this, "onDependentFieldChanged", _descriptor13, this);
    _initializerDefineProperty(this, "validate", _descriptor14, this);
    _initializerDefineProperty(this, "hideValidation", _descriptor15, this);
    _initializerDefineProperty(this, "setUploadingMedia", _descriptor16, this);
    _initializerDefineProperty(this, "discardModifiedAnswer", _descriptor17, this);
    _initializerDefineProperty(this, "setAnswerFromFieldValue", _descriptor18, this);
    _initializerDefineProperty(this, "setChoices", _descriptor19, this);
    this._setChoices = function (choices) {
      var _this$choices;
      (_this$choices = _this.choices).splice.apply(_this$choices, [0, _this.choices.length].concat(_toConsumableArray(choices)));
      var otherChoice = _this._setOtherChoiceLabel(choices);
      _this.otherChoiceId = otherChoice === null || otherChoice === void 0 ? void 0 : otherChoice.id;
    };
    this._setOtherChoiceLabel = function (choices) {
      var otherChoice = choices.find(function (choice) {
        return choice.other;
      });
      if (otherChoice) {
        otherChoice.label = _this.intl.formatMessage(IntlChoiceMessages[ChoiceMessage.OtherPlaceholder]);
      }
      return otherChoice;
    };
    this._isAsyncChoicesQuestion = function () {
      var _this$choices2;
      // for non-async choices fields - all choices are fetched on form load
      return !(((_this$choices2 = _this.choices) === null || _this$choices2 === void 0 ? void 0 : _this$choices2.length) > 0);
    };
    _initializerDefineProperty(this, "setAnswerChoicesAsObjects", _descriptor20, this);
    this._setAnswerChoicesAsObjects = function (answerChoicesAsObjects) {
      var _this$answerChoicesAs;
      (_this$answerChoicesAs = _this.answerChoicesAsObjects).splice.apply(_this$answerChoicesAs, [0, _this.answerChoicesAsObjects.length].concat(_toConsumableArray(answerChoicesAsObjects)));
      _this._setOtherChoiceLabel(_this.answerChoicesAsObjects);
    };
    _initializerDefineProperty(this, "setIssueFieldData", _descriptor21, this);
    _initializerDefineProperty(this, "update", _descriptor22, this);
    this._update = function (constructing, formQuestion, currentAnswer) {
      _this._setChoices(formQuestion.choices || []);
      _this.answer = extractAnswer(formQuestion.type, currentAnswer || FormAnswerEmpty);
      if (!constructing) {
        _this.hideValidation();
        _this.discardModifiedAnswer();
        _this.setLastUpdated();
      }
    };
    this.loadChoicesAsync = loadChoicesAsync;
    this.onFieldChange = onFieldChange;
    this.onFieldDirty = onFieldDirty;
    this.populateDefaultValueOnEmptySearch = populateDefaultValueOnEmptySearch;
    this.id = parseInt(questionKey, 10);
    this.revisionToken = "".concat(this.id, "-").concat(Date.now());
    this.formQuestion = _formQuestion;
    this.conditions = ((_formQuestion$conditi = _formQuestion.conditions) === null || _formQuestion$conditi === void 0 ? void 0 : _formQuestion$conditi.map(function (condition) {
      return parseInt(condition);
    })) || [];
    this.hasJiraField = !!_formQuestion.jiraField;
    this.hasDataConnection = !!_formQuestion.dcId;
    this._setChoices(_formQuestion.choices || []);
    // this set call is for proforma non-linked dropdowns. As they do not have results in fielddata/ nor in formchoices/, so answerChoicesAsObjects will not be set from e.g. loadFieldDataFn for them.
    this._setAnswerChoicesAsObjects(_formQuestion.choices || []);
    this.answer = extractAnswer(_formQuestion.type, _currentAnswer || FormAnswerEmpty);
    switch (_formQuestion.type) {
      case FormQuestionType.TextEmail:
        this.formQuestion.validation.rgx = {
          p: /[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*/
        };
        break;
      case FormQuestionType.TextUrl:
        this.formQuestion.validation.rgx = {
          p: /((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’]))/
        };
        break;
      default:
    }
    this._update(true, _formQuestion, _currentAnswer);
  }
  return _createClass(QuestionStore, [{
    key: "answerModified",
    get: function get() {
      if (this.formQuestion.type === FormQuestionType.Attachment) {
        return this.hasAttachmentFieldModified();
      } else {
        return this.modifiedAnswer !== undefined;
      }
    }
  }, {
    key: "currentAnswer",
    get: function get() {
      if (this.modifiedAnswer !== undefined) {
        return this.modifiedAnswer;
      }
      if (this.answer != null) {
        return this.answer;
      }
      return FormAnswerEmpty;
    }
  }, {
    key: "isAsyncChoicesField",
    get: function get() {
      var _this$issueFieldData, _this$issueFieldData2;
      var linkedJiraFieldSchemaSystem = (_this$issueFieldData = this.issueFieldData) === null || _this$issueFieldData === void 0 || (_this$issueFieldData = _this$issueFieldData.schema) === null || _this$issueFieldData === void 0 ? void 0 : _this$issueFieldData.system;
      if (linkedJiraFieldSchemaSystem && asyncChoicesFieldSchemaSystems.includes(linkedJiraFieldSchemaSystem)) {
        return true;
      }
      var linkedJiraFieldSchemaCustom = (_this$issueFieldData2 = this.issueFieldData) === null || _this$issueFieldData2 === void 0 || (_this$issueFieldData2 = _this$issueFieldData2.schema) === null || _this$issueFieldData2 === void 0 ? void 0 : _this$issueFieldData2.custom;
      if (linkedJiraFieldSchemaCustom && (fg('jcs_master_flag') ? asyncChoicesFieldSchemaCustomsWithEntitlement : asyncChoicesFieldSchemaCustoms).includes(linkedJiraFieldSchemaCustom)) {
        return true;
      }
      return false;
    }
  }, {
    key: "validationErrors",
    get: function get() {
      var _this$issueFieldData3;
      if (this.answerModified || this.validating) {
        var currentAnswerForTextQuestion = toTextQuestionAnswer(this.currentAnswer) || '';
        switch (this.formQuestion.type) {
          case FormQuestionType.TextShort:
          case FormQuestionType.TextLong:
          case FormQuestionType.TextParagraph:
            return getTextValidation(this.formQuestion.validation, currentAnswerForTextQuestion, undefined, this.formQuestion.label);
          case FormQuestionType.RichTextParagraph:
            return getRichTextValidation(this.formQuestion.validation, toJS(toRichTextQuestionAnswer(this.currentAnswer)) || getEmptyADF(), undefined, this.formQuestion.label);
          case FormQuestionType.TextEmail:
            return getTextValidation(this.formQuestion.validation, currentAnswerForTextQuestion, FormQuestionType.TextEmail, this.formQuestion.label);
          case FormQuestionType.TextUrl:
            return getTextValidation(this.formQuestion.validation, currentAnswerForTextQuestion, FormQuestionType.TextUrl, this.formQuestion.label);
          case FormQuestionType.Number:
            return getNumberValidation(this.formQuestion.validation, currentAnswerForTextQuestion, this.formQuestion.label);
          case FormQuestionType.DateTime:
            return getDateTimeValidation(this.formQuestion.validation, toDateTimeQuestionAnswerOrEmpty(this.currentAnswer), this.formQuestion.label);
          case FormQuestionType.Date:
            return getDateValidation(this.formQuestion.validation, currentAnswerForTextQuestion, this.formQuestion.label);
          case FormQuestionType.Time:
            return getTimeValidation(this.formQuestion.validation, currentAnswerForTextQuestion, this.formQuestion.label);
          case FormQuestionType.ChoiceCascading:
          case FormQuestionType.ChoiceSingle:
          case FormQuestionType.ChoiceMultiple:
          case FormQuestionType.ChoiceDropdown:
          case FormQuestionType.ChoiceDropdownMultiple:
          case FormQuestionType.CmdbObject:
            return getChoiceValidation(this.formQuestion.validation, this.choices, toChoiceQuestionAnswerOrEmpty(this.currentAnswer), (_this$issueFieldData3 = this.issueFieldData) === null || _this$issueFieldData3 === void 0 ? void 0 : _this$issueFieldData3.asyncRequiredChoice, this.formQuestion.label);
          case FormQuestionType.UserSingle:
          case FormQuestionType.UserMultiple:
            return getUserPickerValidation(this.formQuestion.validation, toUserPickerQuestionAnswer(this.currentAnswer) || [], this.formQuestion.label);
          case FormQuestionType.Attachment:
            return getAttachmentValidation(this.formQuestion.validation, toJS(toAttachmentQuestionAnswer(this.currentAnswer)) || [], this.formQuestion.type, this.formQuestion.label);
          default:
        }
      }
    }
  }]);
}(), _QuestionStore.optionToChoice = function (option) {
  var _option$children;
  return {
    id: option.value,
    label: option.label,
    children: (_option$children = option.children) === null || _option$children === void 0 ? void 0 : _option$children.map(_QuestionStore.optionToChoice),
    cmdbObject: option.cmdbObject
  };
}, _QuestionStore), (_descriptor = _applyDecoratedDescriptor(_class.prototype, "answer", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "modifiedAnswer", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "validating", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "loading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isUploadingMedia", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "choices", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return [];
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "answerChoicesAsObjects", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return [];
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "dependentFieldChangedCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return 0;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "otherChoiceId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "revisionToken", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "issueFieldData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "answerModified", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "answerModified"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "currentAnswer", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "currentAnswer"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isAsyncChoicesField", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isAsyncChoicesField"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "validationErrors", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "validationErrors"), _class.prototype), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "setLastUpdated", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this2 = this;
    return function () {
      _this2.revisionToken = "".concat(_this2.id, "-").concat(Date.now());
    };
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "setAnswer", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this3 = this;
    return function (answer) {
      var modifiedAnswer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (modifiedAnswer) {
        _this3.modifiedAnswer = answer;
        if (_this3.answerModified) {
          var _this3$onFieldDirty;
          (_this3$onFieldDirty = _this3.onFieldDirty) === null || _this3$onFieldDirty === void 0 || _this3$onFieldDirty.call(_this3);
        }
      } else {
        _this3.modifiedAnswer = undefined;
        _this3.answer = answer;
      }
      if (isChoiceQuestionAnswer(answer) && answer.choiceObjects != null) {
        _this3.setAnswerChoicesAsObjects(answer.choiceObjects);
      }
      _this3.setLastUpdated();
      _this3.onFieldChange({
        jiraField: _this3.formQuestion.jiraField,
        questionType: _this3.formQuestion.type
      }, toTextQuestionAnswer(answer) || '');
    };
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "doPopulateDefaultValueOnEmptySearch", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this4 = this;
    return function () {
      var _this4$issueFieldData;
      if (!_this4.populateDefaultValueOnEmptySearch || !((_this4$issueFieldData = _this4.issueFieldData) !== null && _this4$issueFieldData !== void 0 && _this4$issueFieldData.populateDefaultValueOnEmptySearch) || !_this4.isAsyncChoicesField) {
        return Promise.resolve();
      }
      _this4.loading = true;
      return _this4.loadChoicesAsync('').then(function (options) {
        var _this4$formQuestion$v, _this4$issueFieldData2, _this4$issueFieldData3;
        var choices = options.map(QuestionStore.optionToChoice);
        var maxAllowedChoices = Math.min((_this4$formQuestion$v = _this4.formQuestion.validation.mxs) !== null && _this4$formQuestion$v !== void 0 ? _this4$formQuestion$v : choices.length, (_this4$issueFieldData2 = (_this4$issueFieldData3 = _this4.issueFieldData) === null || _this4$issueFieldData3 === void 0 ? void 0 : _this4$issueFieldData3.maxAllowedChoices) !== null && _this4$issueFieldData2 !== void 0 ? _this4$issueFieldData2 : choices.length);
        var validatedChoices = choices.splice(0, maxAllowedChoices);
        _this4.setAnswer({
          choices: validatedChoices.map(function (_ref) {
            var id = _ref.id;
            return id;
          }),
          choiceObjects: validatedChoices
        }, false);
      }).finally(function () {
        _this4.loading = false;
      });
    };
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "onDependentFieldChanged", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this5 = this;
    return function () {
      _this5.dependentFieldChangedCount++;
    };
  }
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "validate", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this6 = this;
    return function () {
      _this6.validating = true;
    };
  }
}), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "hideValidation", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this7 = this;
    return function () {
      _this7.validating = false;
      _this7.setLastUpdated();
    };
  }
}), _descriptor16 = _applyDecoratedDescriptor(_class.prototype, "setUploadingMedia", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this8 = this;
    return function (isUploadingMedia) {
      _this8.isUploadingMedia = isUploadingMedia;
    };
  }
}), _descriptor17 = _applyDecoratedDescriptor(_class.prototype, "discardModifiedAnswer", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this9 = this;
    return function () {
      _this9.modifiedAnswer = undefined;
      _this9.setLastUpdated();
    };
  }
}), _descriptor18 = _applyDecoratedDescriptor(_class.prototype, "setAnswerFromFieldValue", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this0 = this;
    return function (answer) {
      _this0.answer = extractAnswer(_this0.formQuestion.type, answer);
      _this0.modifiedAnswer = undefined;
      _this0.setLastUpdated();
    };
  }
}), _descriptor19 = _applyDecoratedDescriptor(_class.prototype, "setChoices", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this1 = this;
    return function (choices) {
      // Populate choiceObjects in the existing answer for choices where only the ID was known previously (e.g. default answers).
      var currentAnswerChoices = _this1.getAnswerChoicesAsObjects().map(QuestionStore.optionToChoice);
      var newAnswerChoices = choices.filter(function (choice) {
        var _toChoiceQuestionAnsw;
        return ((_toChoiceQuestionAnsw = toChoiceQuestionAnswer(_this1.currentAnswer)) === null || _toChoiceQuestionAnsw === void 0 ? void 0 : _toChoiceQuestionAnsw.choices.includes(choice.id)) && !currentAnswerChoices.some(function (_ref2) {
          var id = _ref2.id;
          return id === choice.id;
        });
      });
      _this1.setAnswerChoicesAsObjects([].concat(_toConsumableArray(currentAnswerChoices), _toConsumableArray(newAnswerChoices)));
      _this1._setChoices(choices);
      _this1.setLastUpdated();
    };
  }
}), _descriptor20 = _applyDecoratedDescriptor(_class.prototype, "setAnswerChoicesAsObjects", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this10 = this;
    return function (choices) {
      var sortedChoices;
      if (!_this10._isAsyncChoicesQuestion()) {
        // sort in the same order as this.choices from API - to have same sorting order between Open and Submitted form states
        sortedChoices = choices.slice().sort(function (a, b) {
          return _this10.choices.findIndex(function (choice) {
            return choice.id === a.id;
          }) - _this10.choices.findIndex(function (choice) {
            return choice.id === b.id;
          });
        });
      } else {
        sortedChoices = choices.slice().sort(sortById); // same as at the BE
      }
      _this10._setAnswerChoicesAsObjects(sortedChoices);
      _this10.setLastUpdated();
    };
  }
}), _descriptor21 = _applyDecoratedDescriptor(_class.prototype, "setIssueFieldData", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this11 = this;
    return function (issueFieldData) {
      _this11.issueFieldData = issueFieldData;
      _this11.setLastUpdated();
    };
  }
}), _descriptor22 = _applyDecoratedDescriptor(_class.prototype, "update", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    var _this12 = this;
    return function (formQuestion, currentAnswer) {
      return _this12._update(false, formQuestion, currentAnswer);
    };
  }
})), _class);